import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Check if browser supports interpolate-size (Safari doesn't)
const supportsInterpolateSize = typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('interpolate-size', 'allow-keywords');

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    return this.details.querySelector('.details__content');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #isAnimating = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Apply Safari fallback class if needed
    if (!supportsInterpolateSize) {
      this.classList.add('accordion-custom--safari-fallback');
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isCurrentlyOpen = this.details.open;
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // For Safari fallback, handle animation manually
    if (!supportsInterpolateSize && this.content) {
      event.preventDefault();

      if (this.#isAnimating) return;

      if (isCurrentlyOpen) {
        this.#animateClose();
      } else {
        this.#animateOpen();
      }
    }

    // Only run "single open" behaviour when this accordion is being opened
    const isOpening = !isCurrentlyOpen;

    if (!isOpening) return;

    // Close any sibling accordions within the same parent container
    const parent = this.parentElement;

    if (!parent) return;

    /** @type {NodeListOf<HTMLElement>} */
    const siblingAccordions = parent.querySelectorAll('accordion-custom');

    siblingAccordions.forEach((accordion) => {
      if (accordion === this) return;

      const details = accordion.querySelector('details');

      if (details instanceof HTMLDetailsElement && details.open) {
        // For Safari fallback, animate sibling close
        if (!supportsInterpolateSize && accordion instanceof AccordionCustom && typeof accordion.closeWithAnimation === 'function') {
          accordion.closeWithAnimation();
        } else {
          details.open = false;
        }
      }
    });
  };

  /**
   * Animate accordion open (Safari fallback)
   */
  #animateOpen() {
    const content = this.content;
    if (!content) return;

    this.#isAnimating = true;

    // Open details to measure content
    this.details.open = true;

    // Get the natural height
    const height = content.scrollHeight;

    // Set initial state
    content.style.height = '0px';
    content.style.opacity = '0';
    content.style.overflow = 'hidden';

    // Force reflow
    content.offsetHeight;

    // Animate to full height
    content.style.transition = 'height var(--animation-speed-slow, 0.3s) var(--animation-easing, ease), opacity var(--animation-speed-slow, 0.3s) var(--animation-easing, ease)';
    content.style.height = `${height}px`;
    content.style.opacity = '1';

    const onTransitionEnd = () => {
      content.removeEventListener('transitionend', onTransitionEnd);
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
      this.#isAnimating = false;
    };

    content.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Animate accordion close (Safari fallback)
   */
  #animateClose() {
    const content = this.content;
    if (!content) return;

    this.#isAnimating = true;

    // Get current height
    const height = content.scrollHeight;

    // Set explicit height
    content.style.height = `${height}px`;
    content.style.overflow = 'hidden';

    // Force reflow
    content.offsetHeight;

    // Animate to 0
    content.style.transition = 'height var(--animation-speed-slow, 0.3s) var(--animation-easing, ease), opacity var(--animation-speed-slow, 0.3s) var(--animation-easing, ease)';
    content.style.height = '0px';
    content.style.opacity = '0';

    const onTransitionEnd = () => {
      content.removeEventListener('transitionend', onTransitionEnd);
      this.details.open = false;
      content.style.height = '';
      content.style.overflow = '';
      content.style.opacity = '';
      content.style.transition = '';
      this.#isAnimating = false;
    };

    content.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Public method to close accordion with animation (for Safari fallback)
   */
  closeWithAnimation() {
    if (this.#isAnimating || !this.details.open) return;
    this.#animateClose();
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {};

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
